import { MappedEntity } from '@api';
import {
  SkeletonLoader,
  SkeletonLoaderProps,
  Tooltip,
  TooltipTrigger,
  WithNAFallback,
} from '@design';
import { getPriceStamp } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import { Identifier } from '@terminal/hooks/use-live-price';
import { useOneDayReturnLivePrice } from '@terminal/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { globalPriceFormatter } from '@terminal/utils/asset-price/assetPrice';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { PriceCellTooltip } from './price-cell-tooltip/PriceCellTooltip';
import styles from './PriceCell.module.scss';

const skeletonLoaderAreas: SkeletonLoaderProps['areas'] = [
  {
    styles: {
      width: '40px',
      height: '20px',
      borderRadius: '8px',
    },
  },
];

interface PriceCellProps {
  isEditMode?: boolean;
  asset?: MappedEntity;
}

export const PriceCell = ({ isEditMode = false, asset }: PriceCellProps) => {
  const { t } = useTranslation(['widget', 'watchlistManager']);

  const { price, date, isLive, isSubscribing, isLivePriceSupported } =
    useOneDayReturnLivePrice(asset, Identifier.Watchlist);

  const hasData = !!asset && !!price;

  const stamp = getPriceStamp({
    isLivePriceSupported,
    isLive,
    isDisabled: isEditMode,
  });

  if (isSubscribing) {
    return (
      <div className={styles.skeletonLoaderWrapper}>
        <SkeletonLoader areas={skeletonLoaderAreas} />
      </div>
    );
  }

  return (
    <WithNAFallback
      check={hasData}
      tooltip={t('widget:watchlist.noLivePriceDataAvailable')}
      value={
        hasData && (
          <Tooltip
            isTouchDevice={false}
            trigger={TooltipTrigger.Hover}
            label={
              date && <PriceCellTooltip type={stamp.stampType} date={date} />
            }
            inPortal
            disabled={!date}
          >
            <div className={styles.flexWrapper}>
              <div
                className={clsx(styles.priceWrapper, {
                  [styles.isDisabled]: isEditMode,
                })}
              >
                <span>
                  {globalPriceFormatter({
                    price,
                    entity: asset,
                    priceSuffix: '',
                  })}
                </span>
                <span className={styles.currency}>{asset.currency}</span>
              </div>
              <div className={styles.stampWrapper}>
                {stamp.stampType === 'notSupported' ? (
                  <div
                    className={styles.squareStamp}
                    style={
                      { '--stamp-color': stamp.color } as React.CSSProperties
                    }
                  />
                ) : (
                  <div
                    className={styles.circleStamp}
                    style={
                      { '--stamp-color': stamp.color } as React.CSSProperties
                    }
                  />
                )}
              </div>
            </div>
          </Tooltip>
        )
      }
    />
  );
};
